import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BTCategoryResponse,
  BTMenuItemResponse,
  BTMenuResponse,
  Chevron,
  Div,
  DragSortIcon,
  EditPenIcon,
  H5,
  Icon,
  ImagePlaceholderCircle,
  ImagePlaceholderIcon,
  Img,
  P,
  TrashIconRed,
  centsToDollars,
  formatter,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';
import { Collapsible, ItemListImage } from '../../MenyStyled';

export const MenuItemTypeComponent = ({
  item,
  onClick,
  onClickDelete,
  menu,
  isSorting,
  activeId,
  activeCategoryId,
  onDragStart,
  onDragEnd,
}: {
  item: BTCategoryResponse;
  onClick: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  onClickDelete: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  menu: BTMenuResponse | null;
  isSorting: boolean;
  activeId: number | null;
  activeCategoryId: number | null;
  onDragStart: (event: any, categoryId: number) => void;
  onDragEnd: (event: any, categoryId: number) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (ref.current) setHeight(ref?.current?.clientHeight);
  }, [menu]);

  useEffect(() => {
    if (ref.current && isSorting) {
      setExpanded(true);
      setHeight(ref?.current?.clientHeight);
    }
  }, [isSorting]);

  return (
    <>
      <Div
        $box
        $p="16px 24px"
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={expanded && lightTheme.primary}
        $pointer
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Div $flex $alignCenter $gap="8px">
          <P $bold $m="0" $color={expanded && lightTheme.white}>
            {item.name} ({item.items?.length || 0})
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          color={expanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Div>

      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          <DndContext
            onDragEnd={(e) => onDragEnd(e, item.id)}
            onDragStart={(e) => onDragStart(e, item.id)}
          >
            <SortableContext
              items={item.items?.map((task) => task.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {item.items?.map((itemItem) => (
                <MenuItemComponent
                  item={itemItem}
                  category={item}
                  key={itemItem.id}
                  onClick={onClick}
                  onClickDelete={onClickDelete}
                  isSorting={isSorting}
                />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeId && activeCategoryId === item.id ? (
                <MenuItemComponent
                  item={
                    item.items?.find(
                      (t) => activeId === t.id
                    ) as BTMenuItemResponse
                  }
                  category={item}
                  isSorting={isSorting}
                  onClick={() => {}}
                  onClickDelete={() => {}}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
          {item.items?.length === 0 ? (
            <P $m="0" $center>
              No items
            </P>
          ) : null}
        </div>
      </Collapsible>
    </>
  );
};

export const MenuItemComponent = ({
  item,
  onClick,
  onClickDelete,
  key,
  category,
  isSorting,
}: {
  item: BTMenuItemResponse;
  onClick: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  onClickDelete: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  key?: number;
  category: BTCategoryResponse;
  isSorting: boolean;
}) => {
  const {
    setNodeRef,
    listeners,
    attributes,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
  });

  const styles = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 0,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <Div
      ref={setNodeRef}
      style={styles}
      {...attributes}
      key={key}
      $box
      $p="12px 24px "
      $flex
      $alignCenter
      $between
      $mb="8"
      $width="100%"
    >
      <Div
        $width="100%"
        $flex
        $alignCenter
        $gap="16px"
        $pointer
        onClick={() => {
          if (!isSorting) {
            onClick({ item, category });
          }
        }}
        {...(isSorting ? listeners : {})}
        style={{
          cursor: isSorting ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        }}
      >
        {isSorting ? (
          <Icon>
            <DragSortIcon />
          </Icon>
        ) : null}
        <Div $flex>
          {item.imageUrl ? (
            <ItemListImage>
              <Img
                alt=""
                height="50px"
                src={
                  item.imageUrl ? item.imageUrl : (ImagePlaceholderIcon as any)
                }
              />
            </ItemListImage>
          ) : (
            <ImagePlaceholderCircle />
          )}
        </Div>

        <Div $flex $column $gap="2px">
          <H5 $mb="0">{item.name}</H5>
          <P $m="0">
            Suggested Price:{' '}
            <span style={{ color: lightTheme.green }}>
              {formatter.format(centsToDollars(item.fromPrice))} -{' '}
              {formatter.format(centsToDollars(item.toPrice))}
            </span>
          </P>
        </Div>
      </Div>
      <Div $flex $alignCenter $between $gap="16px" $gapSM="8px">
        <Icon $shadow $pointer onClick={() => onClick({ item, category })}>
          <EditPenIcon />
        </Icon>
        <Icon $pointer onClick={() => onClickDelete({ item, category })}>
          <TrashIconRed />
        </Icon>
      </Div>
    </Div>
  );
};
